import { DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SECTION_ID = 'atc-upsell-modal';

/**
 * Dialog shown after an item is added to the cart. It confirms what was added and offers the
 * upsell products stored in the added product's `custom.upsell_products` metafield.
 *
 * The cart drawer already covers this flow when it is configured to auto-open, so the dialog
 * stays out of the way in that case.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} content - The container the section markup is rendered into.
 *
 * @extends {DialogComponent<Refs>}
 */
class AtcUpsellModal extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {string | null} */
  #productUrl = null;

  /** @type {string | null} */
  #variantId = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#fetchController?.abort();
  }

  /**
   * Handles a cart update, either opening the dialog or reflecting an upsell added from within it.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  #onCartUpdate = (event) => {
    const { data, sourceId } = event.detail ?? {};

    if (!data || data.didError || data.source !== 'product-form-component') return;

    const productForm = event.target instanceof Element ? event.target.closest('product-form-component') : null;

    if (productForm && this.contains(productForm)) {
      this.#onUpsellAdded(productForm);
      return;
    }

    if (this.#autoOpeningDrawer || Theme.template.name === 'cart') return;

    this.#open(productForm, data.variantId ?? sourceId);
  };

  get #autoOpeningDrawer() {
    return Boolean(document.querySelector('cart-drawer-component[auto-open]'));
  }

  /**
   * Loads the dialog content for the added product and opens it.
   * @param {Element | null} productForm - The product form the item was added from.
   * @param {string | undefined} variantId - The variant that was added.
   */
  async #open(productForm, variantId) {
    const productUrl = productForm instanceof HTMLElement ? productForm.dataset.productUrl : null;

    if (!productUrl) return;

    this.#productUrl = productUrl;
    this.#variantId = variantId ? String(variantId) : null;

    const html = await this.#fetchContent();

    if (!html || !this.#hasUpsells(html)) return;

    this.refs.content.innerHTML = html;
    this.updatedCallback();
    this.showDialog();
  }

  /**
   * @param {string} html - Section markup.
   * @returns {boolean} Whether the product has available popup upsells.
   */
  #hasUpsells(html) {
    return Boolean(new DOMParser().parseFromString(html, 'text/html').querySelector('.atc-upsell__suggestions'));
  }

  /**
   * Fetches the dialog content from the section rendering API.
   * @returns {Promise<string | null>} The section markup, or null when the request failed.
   */
  async #fetchContent() {
    if (!this.#productUrl) return null;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    if (this.#variantId) url.searchParams.set('variant', this.#variantId);

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });

      if (!response.ok) throw new Error(`Failed to load ${SECTION_ID}: ${response.status}`);

      return await response.text();
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);

      return null;
    }
  }

  /**
   * Marks an upsell as added and refreshes the parts of the dialog that depend on cart totals.
   * @param {Element} productForm - The product form the upsell was added from.
   */
  #onUpsellAdded(productForm) {
    productForm.closest('.cart-item-related-product')?.classList.add('is-added');

    this.#refreshTotals();
  }

  /**
   * Re-renders the added line and the free shipping progress so they match the current cart.
   */
  async #refreshTotals() {
    const html = await this.#fetchContent();

    if (!html) return;

    const updated = new DOMParser().parseFromString(html, 'text/html');

    for (const selector of ['.atc-upsell__added', 'free-shipping-bar']) {
      const current = this.refs.content.querySelector(selector);
      const replacement = updated.querySelector(selector);

      if (current && replacement) current.replaceWith(replacement);
    }
  }

  /**
   * Removes an upsell that was added from within the dialog.
   * @param {MouseEvent} event - The click event.
   */
  removeUpsellItem = async (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('button') : null;
    const row = trigger?.closest('.cart-item-related-product');
    const variantInput = /** @type {HTMLInputElement | null | undefined} */ (row?.querySelector('input[name="id"]'));
    const variantId = variantInput?.value;

    if (!trigger || !row || !variantId) return;

    trigger.disabled = true;

    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      const line = cart.items.findIndex(
        /** @param {{ variant_id: number }} item */ (item) => String(item.variant_id) === variantId,
      );

      if (line === -1) {
        row.classList.remove('is-added');
        return;
      }

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            line: line + 1,
            quantity: Math.max(cart.items[line].quantity - 1, 0),
            sections: this.#cartSectionIds,
            sections_url: window.location.pathname,
          }),
        }),
      );
      const updatedCart = await response.json();

      if (updatedCart.errors) throw new Error(updatedCart.errors);

      row.classList.remove('is-added');

      this.dispatchEvent(
        new CartUpdateEvent({}, this.id, {
          itemCount: updatedCart.item_count,
          source: 'atc-upsell-modal',
          sections: updatedCart.sections,
        }),
      );

      this.#refreshTotals();
    } catch (error) {
      console.error(error);
    } finally {
      trigger.disabled = false;
    }
  };

  /**
   * The sections that render cart contents and therefore need re-rendering after a change.
   * @returns {string} A comma separated list of section ids.
   */
  get #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }
}

if (!customElements.get('atc-upsell-modal')) {
  customElements.define('atc-upsell-modal', AtcUpsellModal);
}
